<template>
    <div class="stats-visits">
        <div class="panes">
            <!-- 左侧：结果表格 -->
            <div class="pane pane-left">
                <div class="toolbar">
                    <el-date-picker
                        v-model="query.range"
                        type="datetimerange"
                        start-placeholder="开始时间"
                        end-placeholder="结束时间"
                        value-format="yyyy-MM-dd HH:mm:ss"
                        align="right"
                    />
                    <el-select v-model="query.source">
                        <el-option value="" label="全部来源" />
                        <el-option value="tiktok" label="tiktok" />
                        <el-option value="pinterest" label="pinterest" />
                        <el-option value="youtube" label="youtube" />
                    </el-select>
                    <el-button @click="loadData" :disabled="loading">{{ loading ? '加载中…' : '加载数据' }}</el-button>
                    <el-button @click="exportCSV" :disabled="rows.length === 0">导出 CSV</el-button>
                </div>

                <div class="table-wrap" v-if="selectedCols.length">
                    <table class="table">
                        <thead>
                            <!-- <tr> -->
                            <draggable tag="tr" :list="selectedCols"
                                :options="{ animation: 150, handle: '.col-handle', direction: 'horizontal' }">
                                <!-- <th class="row-index sticky" key="#">#</th> -->
                                <th v-for="col in selectedCols" :key="col.key"
                                    :style="{ width: (col.width || 120) + 'px' }">
                                    <div class="th-inner col-handle">
                                        <span title="拖拽列">☰</span>
                                        <span class="th-label" :title="col.desc">
                                            {{ col.label }}
                                        </span>
                                    </div>
                                </th>
                            </draggable>
                            <!-- </tr> -->
                        </thead>
                        <tbody>
                            <tr v-for="(row, i) in rows" :key="row.__id">
                                <!-- <td class="row-index">{{ i + 1 }}</td> -->
                                <td v-for="col in selectedCols" :key="col.key" :style="`width:${col.width}px;`">
                                    <span v-if="col.fmt == 'extra_json'">{{ row[col.fmt][col.key] || '-' }}</span>
                                    <span v-else-if="col.key == 'device'">{{['X11', 'iPhone', 'Android', 'Mac',
                                        'Window'].find(f => row['ua'].includes(`${f}`)) || '-'}}</span>
                                    <span v-else-if="col.key == 'referer'">{{
                                        ['tk', 'tiktok', 'pin', 'fb', 'facebook', 'yt', 'youtube'].find(f =>
                                            row['referer'].includes(`utm_source=${f}`)) || '-'}}</span>
                                    <span v-else-if="col.fmt == 'bool' && row[col.key] != null">
                                        <el-icon :name="col.fmt == 'bool' && row[col.key] ? 'success' : 'error'"
                                            :class="[{ 'c-yes': col.fmt == 'bool' && row[col.key], 'c-no': col.fmt == 'bool' && !row[col.key] }]">
                                        </el-icon>
                                    </span>
                                    <span v-else>{{ formatCell(row[col.key], col) }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                     <el-pagination
                        @size-change="handleSizeChange"
                        @current-change="handleCurrentChange"
                        :current-page="currentPage4"
                        :page-sizes="[100, 200, 300, 500]"
                        :page-size="100"
                        layout="total, sizes, prev, next, jumper"
                        :total="200">
                    </el-pagination>
                </div>
                <div v-else class="placeholder">在右侧勾选字段后生成表格（表头可拖拽排序）</div>
            </div>

            <!-- 右侧：字段选择器 -->
            <div class="pane pane-right">
                <div class="picker-toolbar">
                    <input v-model="keyword" placeholder="搜索字段（中文/英文/别名）" />
                    <label><input type="checkbox" v-model="showSelectedOnly"> 只看已选</label>
                </div>

                <div class="sections">
                    <FieldSection title="自建统计接口" :fields="filteredFields('custom')" v-model="checkedKeys" />
                    <FieldSection title="GA4" :fields="filteredFields('ga4')" v-model="checkedKeys" />
                    <FieldSection title="Mixpanel" :fields="filteredFields('mixpanel')" v-model="checkedKeys" />
                </div>

                <div class="selected-box" v-if="selectedCols.length">
                    <div class="selected-title">已选字段（可拖拽排序）</div>
                    <draggable class="chips" :list="selectedCols" :options="{ animation: 150, handle: '.chip' }">
                        <div class="chip" v-for="col in selectedCols" :key="col.key">
                            <span class="chip drag chip-handle">≡</span>
                            <span>{{ col.label }}</span>
                            <button @click="toggle(col.key)">×</button>
                        </div>
                    </draggable>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// 依赖：npm i vuedraggable@2 --save
import draggable from 'vuedraggable'
import {
    LIVE_STATS_PATHS,
    LEGACY_LIVE_STATS_PATHS,
    fetchLiveStatsJsonWithFallback,
} from '@/utils/live-stats-api'

// 字段目录（可根据后端 schema 动态替换）
const FIELD_CATALOG = [
    // 自建统计（你们 /visit/query 返回的主键/指标）
    { key: 'enter_ts', label: '时间', desc: '进入网页时间', width: 150, source: 'custom', fmt: 'time' },
    { key: 'sid', label: '会话ID', desc: 'session id', width: 200, source: 'custom' },
    { key: 'cid', label: '客户端ID', desc: 'client id', width: 200, source: 'custom' },
    { key: 'room_id', label: '房间号', desc: '直播间ID', width: 150, source: 'custom' },
    { key: 'referer', label: '来源', desc: 'tiktok/pinterest/ads…', width: 110, source: 'custom', fmt: 'source' },
    { key: 'ua', label: 'UA', desc: 'User-Agent 摘要', width: 220, source: 'custom' },
    { key: 'device', label: '设备', desc: 'iPhone/Android 型号', width: 160, source: 'custom' },
    { key: 'play_ok', label: '拉流成功', desc: '1 成功 / 0 失败', width: 90, source: 'custom', fmt: 'bool' },
    { key: 'duration_ms', label: '停留时间', desc: '进入 → 退出', width: 90, source: 'custom', fmt: 'time_diff' },
    { key: 't_first', label: '首帧(ms)', desc: 'play_try→success 用时', width: 110, source: 'custom', fmt: 'ms' },
    { key: 'stalls', label: '卡顿次数', desc: 'waiting→playing 次数', width: 90, source: 'custom', fmt: 'int' },
    { key: 'tcplayRefreshCount', label: '重播次数', desc: '3s没有加载视频流重新初始化播放', width: 90, source: 'custom', fmt: 'extra_json' },
    { key: 'danmu', label: '弹幕', desc: '正常加载弹幕', width: 90, source: 'custom', fmt: 'extra_json' },
    { key: 'is_hls_play', label: 'HLS降级', desc: 'webrtc → hls', width: 90, source: 'custom', fmt: 'extra_json' },

    // GA4 指标（以你们服务返回结构为准）
    { key: 'ga_sessions', label: 'GA4 会话数', desc: 'GA4 sessions', width: 110, source: 'ga4', fmt: 'int' },
    { key: 'ga_users', label: 'GA4 用户数', desc: 'GA4 users', width: 110, source: 'ga4', fmt: 'int' },
    { key: 'ga_bounce', label: '跳出率', desc: 'GA4 bounce%', width: 90, source: 'ga4', fmt: 'pct' },

    // Mixpanel 指标
    { key: 'mx_events', label: 'MP 事件数', desc: 'Mixpanel events', width: 110, source: 'mixpanel', fmt: 'int' },
    { key: 'mx_retention', label: '留存率', desc: 'D1 Retention', width: 90, source: 'mixpanel', fmt: 'pct' }
]

// —— 数据层（把 URL 改成你的真实接口即可） —— //
async function fetchCustomAPI(params, fields, base = '') {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value) query.set(key, value)
    })
    const data = await fetchLiveStatsJsonWithFallback(
        base,
        LIVE_STATS_PATHS.visits,
        LEGACY_LIVE_STATS_PATHS.visits,
        query
    )
    const rows = Array.isArray(data.items) ? data.items : []
    const item = rows.map(m => {
        let extraJson = {}
        if (typeof m.extra_json === 'string') {
            try { extraJson = JSON.parse(m.extra_json) || {} } catch (e) { extraJson = {} }
        } else if (m.extra_json && typeof m.extra_json === 'object') {
            extraJson = m.extra_json
        }
        return {
            ...m,
            extra_json: extraJson
        }
    })
    return item
}
async function fetchGA4(params, fields, base = '') {
    return []
    // 例：POST /api/ga4/query
    const url = base + '/api/ga4/query'
    const res = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...params, fields }) })
    if (!res.ok) throw new Error('ga4 http ' + res.status)
    return res.json()
}
async function fetchMixpanel(params, fields, base = '') {
    const url = base + '/api/mixpanel/query_events'
    const res = await fetch(url, { method: 'get', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...params, fields }) })
    if (!res.ok) throw new Error('mixpanel http ' + res.status)
    return res.json()
}
// —— 子组件：字段分组（带复选） —— //
const FieldSection = {
    name: 'FieldSection',
    props: { title: String, fields: Array, value: Array },
    methods: {
        toggle(k) {
            const v = this.value.slice();
            const i = v.indexOf(k); if (i >= 0) v.splice(i, 1); else v.push(k);
            this.$emit('input', v);
        }
    },
    render(h) {
        return h('div', { class: 'section' }, [
            h('div', { class: 'section-title' }, this.title),
            ...this.fields.map(f => h('div', { key: f.key, class: ['field-item', f.checked ? 'on' : ''] }, [
                h('label', [
                    h('input', { attrs: { type: 'checkbox' }, domProps: { checked: f.checked }, on: { change: () => this.toggle(f.key) } }),
                    h('span', { class: 'field-label' }, `${f.label}`),
                    h('span', { class: 'field-key' }, f.key)
                ]),
                h('div', { class: 'field-desc' }, f.desc)
            ]))
        ])
    }
}
// —— 主组件 —— //
export default {
    name: 'StatsVisits',
    components: { draggable, FieldSection },
    props: {
        apiBase: { type: String, default: 'https://streamer.wokfoy.com' } // 可传入后端前缀，如 'https://streamer.wokfoy.com'
    },
    data() {
        return {
            loading: false,
            keyword: '',
            showSelectedOnly: false,
            catalog: FIELD_CATALOG,  // 可在 mounted 里替换为后端 schema
            checkedKeys: [],
            selectedCols: [],
            rows: [],
            query: {
                range:[],
                source: ''
            }
        }
    },
    watch: {
        checkedKeys() { this.syncSelectedCols() }
    },
    async mounted(){
        this.initRange()
        const res = await fetch('https://streamer.wokfoy.com/mixpanel/query_events?start_time='+
        Date.parse(this.query.range[0])+'&end_time='+ Date.parse(this.query.range[1]) + '&event_names=mp_web_page_view', {
            method: 'GET',
            cache: 'no-store'
        });
        const r = await res.json()
        console.log(r)
    },
    methods: {
        initRange() {
            const end = new Date()
            const start = new Date(end.getTime() - 12 * 60 * 60 * 1000)
            this.query.range = [this.formatDateTime(start), this.formatDateTime(end)]
        },
        formatDateTime(date) {
            const pad = n => String(n).padStart(2, '0')
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
        },
        filteredFields(source) {
            return this.catalog           
                .filter(f => f.source === source)
                .filter(f => !this.keyword || [f.key, f.label, f.desc].join('\u0001').toLowerCase().includes(this.keyword.toLowerCase()))
                .filter(f => !this.showSelectedOnly || this.checkedKeys.includes(f.key))
                .map(f => ({ ...f, checked: this.checkedKeys.includes(f.key) }))
        },
        toggle(key) {
            const i = this.checkedKeys.indexOf(key)
            if (i >= 0) this.checkedKeys.splice(i, 1); else this.checkedKeys.push(key)
        },
        syncSelectedCols() {
            const map = new Map(this.selectedCols.map(c => [c.key, c]))
            const next = []
            this.checkedKeys.forEach(k => {
                const found = this.catalog.find(f => f.key === k)
                if (found) next.push(map.get(k) || { ...found })
            })
            this.selectedCols = next
        },

        // —— 核心：按所选字段并发请求三个数据源，行级合并 —— //
        async loadData() {
            if (!this.selectedCols.length) return
            const fields = this.selectedCols.map(c => c.key)
            const params = { from: this.query.range[0], to: this.query.range[1], search: this.query.source || null }

            try {
                this.loading = true
                const [a, b, c] = await Promise.allSettled([
                    fetchCustomAPI(params, fields, this.apiBase),
                    fetchGA4(params, fields, this.apiBase),
                    fetchMixpanel(params, fields, this.apiBase)
                ])
                const A = a.status === 'fulfilled' ? a.value : []
                const B = b.status === 'fulfilled' ? b.value : []
                const C = c.status === 'fulfilled' ? c.value : []

                // —— 合并策略：优先使用自建统计的主键（sid+tsBucket），其次按 index —— //
                const bucket = (ts) => Math.floor((+new Date(ts || 0)) / 5000) // 5s 桶
                const map = new Map()
                const upsert = (obj) => { const k = (obj.sid ? obj.sid : '_') + '|' + (obj.ts ? bucket(obj.ts) : '_'); const old = map.get(k) || { __id: k }; map.set(k, { ...old, ...obj }) }
                A.forEach(upsert); B.forEach(upsert); C.forEach(upsert)
                const merged = Array.from(map.values())
                // 兜底：如果完全没有主键，则按索引合并
                if (merged.length === 0) {
                    const len = Math.max(A.length, B.length, C.length)
                    for (let i = 0; i < len; i++) merged.push({ __id: 'r' + i, ...(A[i] || {}), ...(B[i] || {}), ...(C[i] || {}) })
                }
                this.rows = merged
            } catch (e) {
                console.error(e)
                alert('加载失败：' + e.message)
            } finally {
                this.loading = false
            }
        },
        handleSizeChange(val) {
            console.log(`每页 ${val} 条`);
        },
        handleCurrentChange(val) {
            console.log(`当前页: ${val}`);
        },
        formatCell(val, col) {
            if (val == null) return '-'
            switch (col.fmt) {
                case 'time': return isNaN(val) ? new Date(val).toLocaleString() : new Date(Number(val)).toLocaleString()
                case 'ms': return Number(val).toFixed(0)
                case 'pct': return (Number(val) * 100).toFixed(1) + '%'
                case 'int': return parseInt(val, 10)
                case 'bool': return val ? '√' : '×'
                case 'source': let platformlist = ['tk', 'tiktok', 'pin', 'fb', 'facebook', 'yt', 'youtube']
                    let curr = platformlist.find(f => val.includes(`utm_source=${f}`))
                    let alias = {
                        'tk': 'tiktok',
                        'fb': 'facebook',
                        'yt': 'youtube',
                        'pin': 'pin',
                    }
                    return alias[curr] || curr
                case 'time_diff':
                    let seconds = val
                    if (seconds > 1000) seconds = Math.floor(seconds / 1000);
                    const h = Math.floor(seconds / 3600);
                    const m = Math.floor((seconds % 3600) / 60);
                    const s = seconds % 60;

                    let result = '';
                    if (h > 0) result += `${h}小时`;
                    if (m > 0) result += `${m}分`;
                    result += `${s}秒`;

                    return result;
                default: return val
            }
        },

        exportCSV() {
            if (!this.rows.length) return
            const heads = this.selectedCols.map(c => c.label)
            const lines = [['#', ...heads].join(',')]
            this.rows.forEach((r, i) => {
                const arr = this.selectedCols.map(c => JSON.stringify(r[c.key] || ''))
                lines.push([i + 1, ...arr].join(','))
            })
            const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = 'stats-visits.csv'
            a.click(); URL.revokeObjectURL(a.href)
        }
    }
}

</script>

<style scoped>
.c-yes {
    color: #00ff00;
}

.c-no {
    color: #ff0000;
}

.stats-visits {
    font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial;
}

.panes {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 12px;
}

.pane {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
}

.pane-left {
    padding: 12px;
}

.pane-right {
    padding: 12px;
}

.toolbar {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.table-wrap {
    overflow: auto;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
}

.table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
}

th,
td {
    padding: 8px 10px;
    border-bottom: 1px solid #f5f5f5;
}

th {
    background: #fafafa;
    position: sticky;
    top: 0;
    z-index: 1;
}

.row-index {
    width: 48px;
    position: sticky;
    left: 0;
    background: #fafafa;
    box-shadow: 2px 0 0 #f5f5f5;
}

.col-handle {
    cursor: grab;
    margin-right: 6px;
    color: #888;
}

.th-inner {
    display: flex;
    align-items: center;
    gap: 6px;
}

.placeholder {
    color: #9ca3af;
    padding: 24px;
}

/* 右侧选择器 */
.picker-toolbar {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
}

.picker-toolbar input {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.section {
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    padding: 8px;
    margin-bottom: 10px;
}

.section-title {
    font-weight: 600;
    margin-bottom: 6px;
}

.field-item {
    border: 1px dashed transparent;
    padding: 6px;
    border-radius: 8px;
}

.field-item.on {
    background: #f7faff;
    border-color: #e6f0ff;
}

.field-label {
    margin-left: 6px;
    margin-right: 6px;
}

.field-key {
    color: #9aa0a6;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

.field-desc {
    color: #8b949e;
    font-size: 12px;
    margin-left: 22px;
}

/* 已选 chips */
.selected-box {
    margin-top: 10px;
}

.selected-title {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 6px;
}

.chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
}

.chip.drag {
    cursor: grab;
}

.chip button {
    border: none;
    background: #fff;
    cursor: pointer;
    color: #ef4444;
}
</style>
